import { FC, useState } from 'react';

import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { Company, EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import { EventTypeBackend, TransactionSignatureFields } from '../../../../../types/events.types';
import { getConversionMethodTitle, Safe } from '../../../../../types/safes.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { EventSignatureStatusTableCell, Td } from './table-td';

export const ConvertedSafe: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();
  const { selectedCompany } = Company.useSelected();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);
  const { items, totalPages, isLoading } = EventHooks.useTransactions<
    Safe & TransactionSignatureFields
  >({
    eventId: props.eventId,
    eventType: EventTypeBackend.SAFE_CONVERSION,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  if (props.type !== EventTypeBackend.SAFE_CONVERSION) return <></>;

  const { name } = props;
  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };
  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.SAFE_CONVERSION}
        transactionView={props.transactionView}
        {...tableStateProps}
      >
        {items.map(
          (
            {
              conversionDate,
              stakeholder,
              investment,
              convertedShares,
              conversionMethod,
              capType,
              conversion: { valuation, postConversionSharePrice: eventSharePrice } = {},
              filesLinks,
              docusignFileLinks,
              linkedSignatureFileLink,
              linkedSignatureId,
              signatureStatus,
              signatureSignedCount,
              signatureTotalCount,
            },
            index,
          ) => {
            const transactionDate = conversionDate ?? new Date();

            return (
              <tr key={`${index}_${stakeholder}`}>
                <Td value={format(transactionDate, 'dd/MM/yyyy')} />
                <Td
                  value={<Tooltip content={stakeholder.fullName}>{stakeholder.fullName}</Tooltip>}
                />
                <Td value={investment.toLocaleString('en-US')} />
                <Td
                  value={
                    conversionMethod && (
                      <AppFormattedMessage
                        id={getConversionMethodTitle(conversionMethod, capType)}
                      />
                    )
                  }
                />
                <Td value={valuation?.name} />
                <Td
                  value={`${selectedCompany?.currency?.symbol} ${toMathRound(eventSharePrice).toLocaleString('en-US')}`}
                />
                <Td value={convertedShares?.toLocaleString('en-US')} />
                <EventSignatureStatusTableCell
                  signatureSignedCount={signatureSignedCount}
                  signatureStatus={signatureStatus}
                  signatureTotalCount={signatureTotalCount}
                />
                {!props.transactionView && (
                  <Td
                    value={
                      <EventTransactionFilesCell
                        date={transactionDate}
                        docusignFileLinks={docusignFileLinks}
                        filesLinks={filesLinks}
                        linkedSignatureFileLink={linkedSignatureFileLink}
                        linkedSignatureId={linkedSignatureId}
                        onDocumentClick={handleDocumentClick}
                        onOpenFilesModal={handleOpenFilesModalForRow}
                        signatureStatus={signatureStatus}
                        stakeholderName={stakeholder.fullName}
                      />
                    }
                  />
                )}
              </tr>
            );
          },
        )}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
